import { useRef, useState } from 'react'
import Modal from './Modal'
import { useModalShell } from './useModalShell'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  askForPrivate,
  fetchAskableTeachers,
  fetchMyPrivateRequests,
  type PrivateRequest,
} from '../api/privateRequests'
import { MY_ASKS_KEY } from '../api/asks'
import { fetchContent, text } from '../api/content'
import { clockTime, meridiem } from '../api/attendance'
import { formatFullDate, today } from '../api/calendar'
import { minutesOf } from '../api/freeBusy'
import { X } from 'lucide-react'
 import SchedulingAssistant from './SchedulingAssistant'

/**
 * How long she is asking for, so a gap too short is never suggested.
 *
 * Clamped at zero because the two inputs are independent — an end earlier than
 * its start is a state the form can hold mid-edit, and a negative duration
 * would make every gap look long enough.
 */
export function minutesBetween(start: string, end: string): number {
  return Math.max(0, minutesOf(end) - minutesOf(start))
}

/**
 * The family's side of a private lesson (D-286, D-292).
 *
 * **One panel, two purposes**, exactly as the request itself is: with a missed
 * solo attached it is a makeup — the only thing that can repair a class with no
 * style or level for any rule to read — and without one it is simply asking for
 * a private.
 *
 * **Nothing here is a second makeup panel.** A group absence is repaired by a
 * class that already runs, and the offer above this one is where that lives
 * (D-274). Routing group absences through a private would quietly bypass it.
 */
export function PrivateRequestsPanel({
  dancerId,
  dancerName,
  repairable,
  embedded = false,
}: {
  dancerId: string
  dancerName: string
  /**
   * Rendered inside another disclosure, so it drops its own toggle (D-297).
   *
   * Two levels of "click to open" is one too many — a parent who has already
   * opened *Makeups & private lessons* should not then have to open the thing inside
   * it. The standalone form is kept because nothing else uses it yet and a prop
   * that only ever has one value is a prop nobody can reason about.
   */
  embedded?: boolean
  /**
   * Missed solo or duet sessions this dancer could ask a private for.
   *
   * Supplied by the caller rather than fetched here, because the schedule
   * already knows what she missed and a second opinion about it is a second
   * answer that can disagree (D-275).
   */
  repairable?: { occurrenceId: string; className: string; date: string }[]
}) {
  const [open, setOpen] = useState(false)
  const { data: content } = useQuery({ queryKey: ['content'], queryFn: fetchContent })

  const requests = useQuery({
    queryKey: ['portal-private-requests'],
    queryFn: fetchMyPrivateRequests,
  })

  const mine = (requests.data ?? []).filter((r) => r.dancerId === dancerId)

  /*
   * The banner used to live here and now lives on PortalView (D-297).
   *
   * A signal that means "nothing happens until you answer" cannot sit inside a
   * panel behind a button on one tab — reported from live testing, where a
   * proposed time was found only after picking a dancer, opening Schedule, and
   * scrolling. Tier 1 has to be somewhere she cannot miss (D-288).
   */
  const showing = embedded || open

  return (
    <div className="w-full max-w-full">
      {!embedded && (
        <div className="flex justify-end">
          <button
            type="button"
            aria-expanded={open}
            onClick={() => setOpen((v) => !v)}
            className="rounded-lg border border-line-2 px-3 py-1.5 text-[12.5px] font-semibold text-ink-2 hover:border-ink-3"
          >
            {text(content, 'portal.privates.label', 'Private lessons')}
          </button>
        </div>
      )}

      {showing && (
        <div className={embedded ? 'w-full max-w-full' : 'mt-3 w-full max-w-full rounded-xl border border-line-2 p-3'}>
          {embedded && (
            <p className="mb-2 text-[12px] font-semibold text-ink-3">
              {text(content, 'portal.privates.label', 'Private lessons')}
            </p>
          )}
          {requests.isPending && (
            <p className="py-3 text-center text-[13px] text-ink-3">Loading...</p>
          )}

          {/* A failed lookup never reads as "you have asked for nothing" (D-209). */}
          {requests.isError && (
            <div className="py-3 text-center">
              <p className="text-[13px] text-ink-2">
                {text(
                  content,
                  'portal.privates.unavailable',
                  'We could not check your private lessons right now.',
                )}
              </p>
              <button
                type="button"
                onClick={() => void requests.refetch()}
                className="mt-2 rounded-lg border border-line-2 px-3 py-1.5 text-[12.5px] font-semibold text-ink-2"
              >
                Try again
              </button>
            </div>
          )}

          {!requests.isPending && !requests.isError && (
            <>
              {mine.length === 0 && (
                <p className="py-2 text-[13px] text-ink-3">
                  {text(content, 'portal.privates.none', 'No private lessons requested yet.')}
                </p>
              )}

              <ul className="flex flex-col gap-2">
                {mine.map((request) => (
                  <RequestRow key={request.id} request={request} />
                ))}
              </ul>

              {/* The margin was on the button until D-358; it belongs to this layout. */}
              <div className="mt-3">
                <AskForm dancerId={dancerId} dancerName={dancerName} repairable={repairable ?? []} />
              </div>
            </>
          )}
        </div>
      )}
    </div>
  )
}

function when(date: string, start: string, end: string) {
  return `${formatFullDate(date)}, ${clockTime(start)}${meridiem(start)}–${clockTime(end)}${meridiem(end)}`
}

export function RequestRow({
  request,
  showDancer = false,
}: {
  request: PrivateRequest
  /**
   * Name the child as well as the teacher.
   *
   * <b>Off inside the panel, on wherever the list is the household's.</b> Here a
   * dancer has already been picked, so repeating her name on every row is
   * noise; on a household-wide list a family with four children cannot tell
   * whose time is being talked about.
   */
  showDancer?: boolean
}) {
  return (
    <li className="rounded-lg border border-line-2 p-3">
      <p className="text-[13px] font-semibold wrap-anywhere">
        {showDancer && (
          <>
            {/* Its own node, so the name is findable as exactly the name. */}
            <span>{request.dancerName}</span>
            <span className="text-ink-3"> &middot; </span>
          </>
        )}
        {request.teacherName}
        {request.repairsClassName ? ` · making up ${request.repairsClassName}` : ''}
      </p>

      <p className="mt-0.5 text-[12.5px] text-ink-2">
        {when(request.requestedDate, request.requestedStart, request.requestedEnd)}
      </p>

      {/*
        Where it stands, in the family's words rather than the enum's. Every one
        of these is derived on the server from the booking and the asks on the
        envelope (D-286, D-553), so nothing here can be stale in a way nobody
        notices — and nothing here is a control. Answering, and taking a
        request back, happen on Home, on the same row a teacher answers on her
        own list (D-543).
      */}
      {request.state === 'AwaitingTeacher' && (
        <p className="mt-1 text-[12px] text-ink-3">Waiting for {request.teacherName} to reply.</p>
      )}

      {request.state === 'AwaitingParent' && request.proposedDate && (
        <p className="mt-1 text-[12px] text-ink-2 wrap-anywhere">
          {request.teacherName} offered{' '}
          <strong>
            {when(
              request.proposedDate,
              request.proposedStart ?? '00:00:00',
              request.proposedEnd ?? '00:00:00',
            )}
          </strong>{' '}
          instead. Answer it on Home.
        </p>
      )}

      {request.state === 'Accepted' && (
        <p className="mt-1 text-[12px] font-semibold text-good">Confirmed.</p>
      )}

      {request.state === 'Expired' && (
        <p className="mt-1 text-[12px] text-ink-3">
          {/* Silence is never a yes (D-286) — and it must not look like one. */}
          Not answered before the day, so it did not go ahead. You can ask again.
        </p>
      )}

      {request.state === 'Withdrawn' && (
        <p className="mt-1 text-[12px] text-ink-3">You cancelled this.</p>
      )}

      {/*
        <b>`Cancelled` deliberately has no row here</b> (D-690).

        The state is real and the endpoint returns it, but this component has
        been mounted by nothing since D-553 — so a sentence written here would
        be a sentence no parent can read, which is the precise trap D-691 was
        caught walking into on this very file. Writing the copy now, against a
        screen that may never come back, would be guessing at what it should
        say from a distance.

        Where a family actually reads a cancelled private is her schedule, and
        there it is a whole row that stops appearing.
      */}
      {request.state === 'Declined' && (
        <p className="mt-1 text-[12px] text-ink-3 wrap-anywhere">
          {/* The reason, always — a "no" nobody explained gets asked again (D-163). */}
          Not going ahead{request.declineReason ? `: ${request.declineReason}` : '.'}
        </p>
      )}
    </li>
  )
}

export function AskForm({
  dancerId,
  dancerName,
  repairable,
  presetRepairs,
  trigger = 'button',
  open = false,
  initialNote,
  paysFromCredit = false,
  onClose,
  onRaised,
}: {
  dancerId: string
  dancerName: string
  repairable: { occurrenceId: string; className: string; date: string }[]
  /**
   * Whether a private for this dancer comes out of credit on the account
   * (D-662), as the **server** answered it — `PortalDancer.paysFromCredit`.
   *
   * ⚠ **Defaults to false, and the direction of that default is deliberate.**
   * False hides the account-credit sentence, so a call site that forgets to pass
   * this can only ever say *less* than it might — and under-disclosing is the
   * failure the owner asked for (D-836), where over-disclosing was the bug. The
   * cost is that a credit family would read the general sentence, which is still
   * true of her; the reverse default would tell every family the option exists.
   */
  paysFromCredit?: boolean
  /**
   * Opened already, for a caller that is the door (D-630): a conversation
   * offers this from a chip, so there is no button to press first. Closing
   * it, sent or not, tells the caller through `onClose`, which is how the
   * caller knows to take it off the screen rather than show the button.
   */
  open?: boolean
  /** Her words, carried in as the note (D-630); still hers to change. */
  initialNote?: string
  onClose?: () => void
  /** The ask's id once it exists (D-630), so the caller can pin it. Before `onClose`. */
  onRaised?: (askId: string) => void
  /**
   * The absence this ask starts out repairing (D-405).
   *
   * <b>Set when the parent arrived from a particular missed row</b>, which is
   * the whole reason that row offers this. She pointed at one date and said
   * *repair this*; making her pick it again out of a list she has already
   * filtered is the same mistake D-340 fixed on the group path, where booking
   * the oldest mended a different gap than the one she tapped.
   */
  presetRepairs?: string
  /**
   * <b>The accent button, or a quiet inline link.</b> The schedule header's
   * copy sits beside *Report an absence* and is the affirmative half of that
   * pair (D-383). The one on a missed row sits under a date, in a list, where
   * an accent button per absence would give a family with four of them four
   * competing calls to action and no hierarchy at all.
   *
   * A variant rather than a second component: the form below is identical, and
   * two copies of it would drift the moment one gained a field.
   */
  trigger?: 'button' | 'inline'
}) {
  const queryClient = useQueryClient()
  const { data: content } = useQuery({ queryKey: ['content'], queryFn: fetchContent })
  const [showing, setShowing] = useState(open)
  const [teacherStaffId, setTeacher] = useState('')
  const [date, setDate] = useState(today())
  const [start, setStart] = useState('17:00')
  const [end, setEnd] = useState('17:30')
  const [repairs, setRepairs] = useState(presetRepairs ?? '')
  const [note, setNote] = useState(initialNote ?? '')

  // The last step before the request goes (D-697, item 36).
  const [confirming, setConfirming] = useState(false)

  // One way out, whichever control took it, so a caller that is the door
  // always hears (D-630).
  const close = () => {
    setShowing(false)
    onClose?.()
  }

  const teachers = useQuery({
    queryKey: ['portal-askable-teachers'],
    queryFn: fetchAskableTeachers,
    enabled: showing,
  })

  const ask = useMutation({
    mutationFn: () =>
      askForPrivate({
        dancerId,
        teacherStaffId,
        date,
        start: `${start}:00`,
        end: `${end}:00`,
        repairsOccurrenceId: repairs || null,
        note: note || null,
      }),
    onSuccess: (raised) => {
      setNote('')
      // Back to where it opened, not to blank: on a missed row this form only
      // ever means that one absence, and clearing it would leave a second ask
      // silently repairing nothing (D-405).
      setRepairs(presetRepairs ?? '')
      // Last, because a caller that is the door unmounts this on close (D-630).
      onRaised?.(raised.askId)
      close()
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['portal-private-requests'] })
      queryClient.invalidateQueries({ queryKey: [...MY_ASKS_KEY] })
    },
  })

  const control =
    'rounded-lg border border-line-2 bg-surface px-2.5 py-1.5 text-[13px] outline-none focus:border-accent'

  /*
   * Escape, the focus trap and the scroll lock come from the shared shell
   * (D-515).
   *
   * This comment used to say `ReportAbsence` beside it had no key handler and
   * that this was "a gap there rather than a precedent worth copying". The gap
   * outlived the comment by several decisions, which is most of why the sweep
   * that closed it exists. Both are on the same contract now.
   */
  const pressedBackdrop = useRef(false)
  const panel = useRef<HTMLDivElement>(null)

  useModalShell({ panel, onDismiss: () => close(), active: showing })

  if (!showing) {
    return (
      /*
       * **No outer margin. Spacing belongs to whoever is doing the laying
       * out** (D-358).
       *
       * `mt-3` lived here for `PrivateRequestsPanel` below, where this button
       * follows a list and has to clear it. On the parent's schedule it is a
       * sibling of *Report an absence* in a flex row, and the margin pushed it
       * 12px lower than the button beside it — reported from the deployed site
       * with a screenshot, and measured at 390px and 1280px before and after.
       *
       * **The panel is test-only today** — `PortalView` imports `AskForm` and
       * `RequestRow`, never the panel — so its `mt-3` wrapper ships nowhere
       * yet. It is still the right place for it: the margin was always that
       * layout's business, and a component that carries one caller's spacing
       * is wrong the moment a second appears, which is how this one broke.
       *
       * **The label is a content block now**, which it should always have been:
       * it sat as a literal directly beside `portal.actions.report`, which is
       * one (A-16). Every hardcoded string is a support call Tonya cannot
       * answer herself.
       */
      /*
        **Pink, and outlined, and so is its sibling** (D-500, amending D-383).

        D-383 made this one filled and left *Report an absence* outlined,
        borrowing the teaching tab's convention — *Book a room* is `bg-accent`,
        *I am out* is outlined — and reasoning that two accents would leave the
        row with three and the accent meaning nothing. The half about *this*
        button was right. The half about the other one was not: on the shipped
        screen the outlined one disappeared, reported as *"otherwise its kind of
        hidden/blends in"*.

        **So the pair moved together rather than one of them moving twice.**
        Filling both would have produced the three accents D-383 warned about;
        outlining both keeps them pink, keeps them identical, and leaves the
        solid fill on this screen meaning exactly one thing. Which button is the
        affirmative one is carried by the words on it, which is where a parent
        was reading it anyway.

        **Identical borders on both**, so they measure level. These two not
        lining up is what D-358 was reported for, and it is the fault D-383
        found while making them differ.
       */
      <button
        type="button"
        onClick={() => setShowing(true)}
        className={
          trigger === 'inline'
            ? /*
                Matching *Find a makeup* on the technique rows exactly (D-405).
                The two sit in the same list, one under each missed date, and
                they are the same offer — here is how you repair this one. A
                different weight or colour would read as a different kind of
                thing, which is the opposite of true.
              */
              'mt-1 -mx-1 rounded-md px-1 py-1 text-[12px] font-semibold text-accent hover:underline'
            : // Outlined, matching *Report an absence* beside it (D-500). Both
              // filled left the parent's schedule with two solid accents plus a
              // solid *Today*; one treatment for the pair keeps the fill
              // meaning one thing. Identical borders, so they measure level.
              'rounded-lg border border-accent px-3 py-1.5 text-[12.5px] font-semibold text-accent hover:bg-accent-soft'
        }
      >
        {trigger === 'inline'
          ? text(content, 'portal.makeup.bookPrivate', 'Book a private makeup')
          : text(content, 'portal.actions.private', 'Schedule a private')}
      </button>
    )
  }

  return (
    /*
     * <b>A dialog, because the assistant inside it needs the room</b> (D-431).
     *
     * This was an inline card and it sat in the same flex row as the Day / Week
     * / Month switcher, so on a 1440px window it measured <b>537px at x=244</b>
     * with the toolbar shoved along beside it and two-thirds of the page empty.
     * That is the half a screenshot shows.
     *
     * <b>The half it does not show is worse.</b> The scheduling assistant
     * renders in here once a teacher is chosen, and it is a fortnight of
     * availability — D-426 widened the teacher's dialog to 46rem for exactly
     * that content, and this had 537px in a stranded column. Same feature, two
     * thirds the width.
     *
     * <b>Shaped like `ReportAbsence`, which is the button beside it</b>: a
     * bottom sheet on a phone, centred above `sm`. Matching the parent
     * portal's own idiom beats matching the teacher's, which a parent never
     * sees.
     */
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-black/45 sm:items-center"
      /*
        The press is remembered as well as the click (D-515). A click is
        dispatched at the nearest common ancestor of its press and release, so
        selecting text in the note and overshooting onto the dim area retargets
        the click here and discards what a parent was typing.
      */
      onMouseDown={(e) => {
        pressedBackdrop.current = e.target === e.currentTarget
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget && pressedBackdrop.current) close()
      }}
    >
      <div
        ref={panel}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-label={`Ask for a private for ${dancerName}`}
        onClick={(e) => e.stopPropagation()}
        /*
          `w-full` with the max, never `mx-auto` alone: an auto margin on the
          cross axis of a flex column defeats `align-items: stretch` and the box
          shrink-wraps to its content (D-254, D-255).
        */
        /*
          <b>Not the scroll container</b> (D-434). It was `overflow-y-auto` with
          a `sticky` footer inside, and a sticky element still occupies the
          scroll box — so the last input sat <i>under</i> the action bar at every
          width measured, unreachable even scrolled to the end. Short content
          showed the same fault as a note field jammed into the footer's border.

          Three siblings instead: a header that stays, a body that scrolls, and
          a footer that stays. Overlap is not something to get right, it is
          something the structure makes impossible.
        */
        className="flex max-h-[90vh] w-full max-w-[46rem] flex-col rounded-t-2xl border border-line bg-surface sm:rounded-2xl"
      >
        <div className="flex items-start justify-between gap-3 px-5 pt-5 pb-2">
          <h2 className="font-display text-lg wrap-anywhere">A private for {dancerName}</h2>

          <button
            type="button"
            onClick={() => close()}
            aria-label="Close"
            className="-mt-1 -mr-1 flex size-9 shrink-0 items-center justify-center rounded-lg text-ink-3 hover:bg-surface-2"
          >
            <X size={17} />
          </button>
        </div>

        {/* The only thing that scrolls. `min-h-0` or a flex child refuses to. */}
        <div className="flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto px-5 pb-4">

      <select
        value={teacherStaffId}
        onChange={(e) => setTeacher(e.target.value)}
        aria-label="Teacher"
        className={`w-full ${control}`}
      >
        <option value="">Which teacher?</option>
        {(teachers.data ?? []).map((t) => (
          <option key={t.id} value={t.id}>
            {t.name}
          </option>
        ))}
      </select>

      {/*
        Solos, duets and comp routines, and the list comes from the schedule
        rather than from a second lookup. A **technique** absence belongs in the
        makeup offer above, which finds a class that already runs (D-274) — a
        routine has no such class, which is why it moved here (D-405).
      */}
      {repairable.length > 0 && (
        <select
          value={repairs}
          onChange={(e) => setRepairs(e.target.value)}
          aria-label="Making up"
          className={`w-full ${control}`}
        >
          <option value="">Not making anything up</option>
          {repairable.map((r) => (
            <option key={r.occurrenceId} value={r.occurrenceId}>
              {r.className} · {formatFullDate(r.date)}
            </option>
          ))}
        </select>
      )}

      {/*
        **The whole point of this feature, and it sits above the inputs
        deliberately.** Asking for a private used to be three blank fields and
        a guess, then a text message to find out the teacher was already
        teaching. The suggestions answer most asks in one tap; the grid is how
        somebody picks a time the list did not offer.

        The manual inputs stay below, and stay usable. They are the fallback
        when the feed refuses (a teacher this family may not ask about) and the
        way to ask for something outside the fortnight the assistant covers.
      */}
      <SchedulingAssistant
        dancerIds={[dancerId]}
        teacherStaffId={teacherStaffId}
        minutes={Math.max(15, minutesBetween(start, end))}
        picked={{ date, start: `${start}:00` }}
        teacherLabel={teachers.data?.find((t) => t.id === teacherStaffId)?.name ?? 'Teacher'}
        onPick={(pickedDate, pickedStart, pickedEnd) => {
          setDate(pickedDate)
          setStart(pickedStart.slice(0, 5))
          setEnd(pickedEnd.slice(0, 5))
        }}
      />

      {/*
        Named, because three bare inputs in a row are "date", "time" and "time"
        to a screen reader and there is nothing to say which end is which.
      */}
      <div className="flex flex-wrap gap-2">
        <input
          type="date"
          aria-label="Date"
          value={date}
          onChange={(e) => setDate(e.target.value)}
          className={control}
        />
        <input
          type="time"
          aria-label="Start time"
          value={start}
          onChange={(e) => setStart(e.target.value)}
          className={control}
        />
        <input
          type="time"
          aria-label="End time"
          value={end}
          onChange={(e) => setEnd(e.target.value)}
          className={control}
        />
      </div>

      <input
        value={note}
        onChange={(e) => setNote(e.target.value)}
        placeholder="What would you like to work on?"
        aria-label="What would you like to work on?"
        className={`w-full ${control}`}
      />

        {/*
          <b>Not while the confirmation is up</b> (D-697). Every send now goes
          through that dialog, so this copy would be behind its backdrop on
          every failure - a second alert nobody can read, and two nodes for one
          message. The dialog carries it instead.
        */}
        {ask.isError && !confirming && (
          <p role="alert" className="text-[12px] font-semibold text-warn">
            {(ask.error as Error).message}
          </p>
        )}
      </div>

      {/*
        <b>Always on screen, and outside the scrolling part</b> (D-431, D-434).
        The assistant is deliberately above the inputs — it answers most asks in
        one tap — which puts a fortnight of rows between opening the dialog and
        the button that sends it, so the action cannot live at the end of the
        scroll. It was `sticky` inside the scroll box first, which kept it in
        view and covered the last input with it.
      */}
      <div className="flex flex-col gap-2 border-t border-line px-5 py-3">
        {/*
          <b>Told before she asks, not after somebody says yes</b> (D-686).

          `asked`, and asked twice: item 14's words were *"parent is told at
          booking that they pay out of pocket"*, and the follow-up was *"nothing
          said hey you need to pay the teacher when you book it"*. The telling
          was built - accepting the request raises the payment ask, which sends
          the family a message with the teacher's link (D-666) - and the *at
          booking* half never was. A family met the fact for the first time in
          an email about a lesson they had already committed to.

          <b>In the footer rather than beside the teacher dropdown</b>, because
          the footer is the part that never scrolls (D-431, D-434): the
          assistant above can put a fortnight of rows between the top of this
          dialog and the button, so anything up there is a sentence she may
          never reach.

          <b>It names no handle and promises no particular answer</b>, and an
          audit is why. The first cut read *"paid directly to the teacher"* and
          *"we will send you her payment details"*, and both can be false. A
          dancer who holds account credit is told by the message that there is
          nothing to pay and the studio will take it from her balance (D-662) -
          so the screen would have said the exact thing D-662 was written to
          stop the app saying, three days after it was written. And when the
          teacher has set no link, what actually goes out is *"she will let you
          know how she takes payment"*, which is not details we are sending.
          <b>What is true in all three cases is that the studio does not bill
          for it</b>, so that is what this says.

          <b>There is also no lesson yet</b> - the teacher may decline or
          counter - so a Venmo handle beside a button that only sends a question
          would read as a bill for something nobody has agreed to.

          ⚠ <b>The hedge covered three cases by naming one of them to everybody,
          and that was the defect</b> (D-836). It ended *"or that it comes out of
          your dancer's account credit"* on every family's screen, whether or not
          she holds any - `asked`, 2026-09-27: *"this language should ONLY show
          for the people who have the flag set that its able to be paid that way,
          owner doesn't want everyone know that its even an option"*.

          <b>So the server says which family this is</b> (`paysFromCredit`) and
          each one gets the definite sentence that is true for her. That is
          better copy than the hedge as well as a smaller disclosure: a family
          who does hold credit now reads what will actually happen instead of a
          two-way maybe. **The flag is per dancer** (D-662), and this form is
          always about one dancer, so the granularity already matches.
        */}
        <p className="text-[12px] text-ink-2">
          Private lessons are <strong>not billed through the studio</strong>.{' '}
          {paysFromCredit
            ? 'If she accepts, it comes out of your dancer’s account credit.'
            : 'If she accepts, we will tell you how she takes payment.'}
        </p>

        {/*
          <b>The same fact again, as a step she has to answer</b> (D-697,
          item 36).

          <b>Reported by a tester, and it is the owner's original ask coming
          back.</b> Item 36 said a parent booking a private *"must be met with a
          modal or in-app notification"*; D-686 built the footer line instead
          and recorded a reason — that a dialog would *"interrupt to repeat
          something she has read"*. She had not read it. The line above is
          12px in a footer under a scheduling assistant, and the tester's words
          were that it *"wasn't visible enough"*.

          <b>A step rather than a louder line.</b> Making the paragraph bigger
          asks her to notice something; a confirmation asks her to answer it,
          and the difference is whether anybody can later say they were not
          told. It is the last thing between her and the request, which is the
          moment item 36 named.

          <b>It says the same thing the footer says, in the same three-way
          shape</b> (D-662): the studio does not bill for it, and what happens
          next depends on whether her dancer holds credit. There is no lesson
          yet - the teacher may decline or counter - so it still names no
          handle and promises no particular answer.
        */}
        {confirming && (
          <Modal
            onDismiss={() => setConfirming(false)}
            label="Before you send this"
            panelClassName="w-full max-w-[26rem]"
          >
            <div className="w-full rounded-2xl border border-line bg-surface px-5 py-5">
              {/*
                ⚠ <b>The heading was false for a family holding credit</b> (D-836).
                "This one is paid to the teacher" is the exact assertion D-662
                exists to stop the app making to her - and unlike the paragraph
                below it, it was not even hedged. `AskRow` already picks its
                heading from the route for the teacher-arranged path; this is the
                same move on the family-arranged one.
              */}
              {/*
                <b>Worded identically to `AskRow`'s</b>, which asks the same
                question at the same moment in the same flow — *your* credit, not
                *her* credit. An audit caught the two a single word apart, which is
                the kind of difference a reader assumes is meaningful.
              */}
              <h3 className="font-display text-xl text-ink">
                {paysFromCredit
                  ? 'This one comes out of your credit'
                  : 'This one is paid to the teacher'}
              </h3>

              <p className="mt-2 text-[13.5px] text-ink">
                Private lessons are <strong>not billed through the studio</strong>.{' '}
                {paysFromCredit
                  ? 'If she accepts, it comes out of your dancer’s account credit.'
                  : 'If she accepts, we will tell you how she takes payment.'}
              </p>

              <p className="mt-2 text-[12.5px] text-ink-3">
                Nothing is booked yet. She may say no, or offer a different time.
              </p>

              {/*
                <b>The refusal has to be inside the overlay</b> (D-697, D-209).

                The form's own error sits under the note field, which is now
                behind a dim backdrop — so a failed send left her pressing a
                live button and told nothing at all. Caught by an audit
                reasoning about the stacking context rather than by a test,
                because occlusion is exactly what jsdom cannot show.
              */}
              {ask.isError && (
                <p className="mt-2 text-[12.5px] font-semibold text-crit" role="alert">
                  {ask.error instanceof Error
                    ? ask.error.message
                    : 'That could not be sent. Try again in a moment.'}
                </p>
              )}

              <div className="mt-4 flex flex-wrap gap-2">
                <button
                  type="button"
                  disabled={ask.isPending}
                  onClick={() => ask.mutate()}
                  className="min-h-[36px] rounded-lg bg-accent px-3.5 text-[12.5px] font-semibold text-on-accent disabled:opacity-40"
                >
                  {ask.isPending ? 'Sending…' : 'Got it, send the request'}
                </button>

                <button
                  type="button"
                  onClick={() => setConfirming(false)}
                  className="min-h-[36px] rounded-lg border border-line-2 px-3.5 text-[12.5px] font-semibold text-ink-3"
                >
                  Back
                </button>
              </div>
            </div>
          </Modal>
        )}

        <div className="flex flex-wrap gap-2">
        <button
          type="button"
          disabled={ask.isPending || !teacherStaffId}
          onClick={() => setConfirming(true)}
          className="min-h-[36px] rounded-lg bg-accent px-3.5 text-[12.5px] font-semibold text-on-accent disabled:opacity-40"
        >
          {ask.isPending ? 'Sending…' : 'Send request'}
        </button>

        <button
          type="button"
          onClick={() => close()}
          className="min-h-[36px] rounded-lg border border-line-2 px-3.5 text-[12.5px] font-semibold text-ink-3"
        >
          Cancel
        </button>
        </div>
      </div>

      </div>
    </div>
  )
}
